import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { of } from 'rxjs';
import { map, mergeMap, catchError } from 'rxjs/operators';
import {
  FeeLookup,
  FeeLookupSuccess,
  FeeLookupFailed,
  TransactionActions,
  SendValidateFailed,
  SendValidateSuccess,
  SendCommitSuccess,
  SendCommitFailed,
  TransactionStatusSuccess,
  TransactionStatusFailed,
  ConfirmRefundSuccess,
  GetRefundSuccess,
  GetRefundFailed,
  ConfirmRefundFailed,
  PrepareTransaction,
} from './transaction.actions';
import { EnvService } from '../../services/env.service';
import { FeeLookUpRequest, SendValidateResponse } from './types';
import {
  QueryParamsState,
  SetSearchParams,
  SetTokenParam,
} from '../query-params';
import { GFFP_FIELDS_CATEGORY } from '../../gffp/gffp.models';
import { select, Store } from '@ngrx/store';

@Injectable()
export class TransactionEffects {
  private env!: string;
  private sendValidateUrl(endpoint: string): string {
    // TODO:TIM Requirements changed, so this can probably be refactored into a single string value, and just interpolate the v2 or v3
    return endpoint === 'v3'
      ? `${this.envService.apiUrl}/v3/transactions/`
      : `${this.envService.apiUrl}/v2/transactions/`;
  }

  public feeLookUp$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FeeLookup),
      mergeMap(({ feeLookupRequest, endpoint }) => {
        return this.http
          .post(`${this.sendValidateUrl(endpoint)}quote`, feeLookupRequest)
          .pipe(
            map((data) => {
              return FeeLookupSuccess({ feeLookupResponse: data });
            }),
            catchError((error) => of(FeeLookupFailed({ error })))
          );
      })
    )
  );

  public sendValidate$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TransactionActions.sendValidate),
      mergeMap(({ transactionId, payload, endpoint, operation }) => {
        return this.http
          .put(`${this.sendValidateUrl(endpoint)}${transactionId}?operation=${operation}`, payload)
          .pipe(
            map((data) => {
              if (payload['destinationCountryCode'] === 'HUN') {
                data = this.checkForHungaryFields(data);
              }
              return SendValidateSuccess({ sendValidateResponse: data });
            }),
            catchError((error) => of(SendValidateFailed({ error })))
          );
      })
    )
  );

  public sendCommit$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TransactionActions.sendCommit),
      mergeMap(({ transactionId, isQrCodeRequired, locale, endpoint }) => {
        return this.http
          .post(
            `${this.sendValidateUrl(
              endpoint
            )}${transactionId}/commit?isQrCodeRequired=${isQrCodeRequired}`,
            { userLanguage: locale }
          )
          .pipe(
            map((data) => {
              return SendCommitSuccess({ sendCommitResponse: data });
            }),
            catchError((error) => of(SendCommitFailed({ error })))
          );
      })
    )
  );

  private getTransactionStatusUrl(endpoint: string): string {
    // TODO:TIM Requirements changed, so this can probably be refactored into a single string value, and just interpolate the v2 or v3
    return endpoint === 'v3'
      ? `${this.envService.apiUrl}/v3/transactions/`
      : `${this.envService.apiUrl}/v2/transactions/`;
  }

  public transactionStatus$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TransactionActions.transactionStatus),
      mergeMap(({ transactionId, refNumber, endpoint }) => {
        const trnStatusURL = refNumber
          ? `${this.getTransactionStatusUrl(
              endpoint
            )}${transactionId}?refNumber=${refNumber}`
          : `${this.getTransactionStatusUrl(endpoint)}${transactionId}`;
        return this.http.get(trnStatusURL).pipe(
          map((data) => {
            return TransactionStatusSuccess({
              transactionStatusResponse: data,
            });
          }),
          catchError((error) => of(TransactionStatusFailed({ error })))
        );
      })
    )
  );

  private refundUrl(endpoint: string): string {
    // TODO:TIM Requirements changed, so this can probably be refactored into a single string value, and just interpolate the v2 or v3
    return endpoint === 'v3'
      ? `${this.envService.apiUrl}/v3/refund/transactions/`
      : `${this.envService.apiUrl}/v2/refund/transactions/`;
  }

  public getRefundDetails$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TransactionActions.getTransactionRefund),
      mergeMap(({ transactionId, refundCancelReason, endpoint }) => {
        return this.http
          .get(
            `${this.refundUrl(
              endpoint
            )}${transactionId}?refundReasonCode=${refundCancelReason}`
          )
          .pipe(
            map((data) => {
              return GetRefundSuccess({ getRefundResponse: data });
            }),
            catchError((error) => of(GetRefundFailed({ error })))
          );
      })
    )
  );

  public confirmRefund$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TransactionActions.confirmTransactionRefund),
      mergeMap(({ transactionId, payload, endpoint }) => {
        return this.http
          .put(`${this.refundUrl(endpoint)}${transactionId}/commit`, payload)
          .pipe(
            map((data) => {
              return ConfirmRefundSuccess({ confirmRefundResponse: data });
            }),
            catchError((error) => of(ConfirmRefundFailed({ error })))
          );
      })
    )
  );

  public prepareTransaction$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SetTokenParam),
      mergeMap(({ queryParams, endpoint, isTransactionStatus }: any) => {
        return this.http
          .post(
            `${this.envService.apiUrl}/v3/transactions/${
              isTransactionStatus ? 'preparestatus' : 'prepare'
            }`,
            {}
          )
          .pipe(
            map((data: any) => {
              let params: QueryParamsState = {
                queryParams: {
                  assetCode: data.assetCode,
                  locale: queryParams.queryParams?.locale || data.lang,
                  operation: data.operation,
                  transactionId: data.transactionId,
                  walletProvider: data.walletProvider,
                  token: queryParams.queryParams.token,
                  endpoint: queryParams.queryParams.endpoint,
                  amount: data.amount
                }
              };
              return SetSearchParams({ queryParams: params, endpoint });
            }),
            catchError((error) => of(SendValidateFailed({ error })))
          );
      })
    )
  );
  checkForHungaryFields(data: any) {
    if (
      data &&
      !!data?.errors &&
      data?.errors[0]?.category && 
      GFFP_FIELDS_CATEGORY.includes(data?.errors[0]?.category)
    ) {
      const isMaidenNameExists = data.errors
        .find((error: any) => error.category && GFFP_FIELDS_CATEGORY.includes(error.category))
        ?.offendingFields?.some(
          (fieldObj: { field: any }) =>
            fieldObj.field === 'sender.additionalFamilyNames.mothersMaidenName'
        );
      if (!isMaidenNameExists) {
        data.errors[0].offendingFields.push({
          field: 'sender.additionalFamilyNames.mothersMaidenName',
        });
      }
    }
    return data;
  }

  constructor(
    private actions$: Actions,
    private http: HttpClient,
    private envService: EnvService,
    private store: Store
  ) {}
}
